// The controls the solution search reports as missing (formulas collectMissingFields, HTML control ids): the set the
// page provides to the input panels (field-missing highlight), and each one's label and input panel ("missing fields"
// links).
import { createContext, useContext } from "react";
import type { Translation } from "../../../hooks/useTranslations";
import type { InputSectionId } from "./types";

export const ScMissingContext = createContext<ReadonlySet<string>>(new Set());

// isMissing(control, empty): the field is highlighted while listed as missing and still empty
export function useMissing() {
  const missing = useContext(ScMissingContext);
  return (control: string, empty: boolean) => empty && missing.has(control);
}

type CalcT = Translation["screwConnectorModule"]["calculation"];

const stripTags = (html: string) => html.replace(/<[^>]*>/g, "");

export function missingFieldInfo(id: string, t: CalcT): { label: string; section: InputSectionId } {
  // element number: the 1 / 2 before the next capital or at the end ("clt2Spessore0", "rhok1")
  const element = /[12](?=[A-Z]|$)/.exec(id);
  const n: 1 | 2 = element?.[0] === "2" ? 2 : 1;
  const elementSection: InputSectionId = n === 1 ? "secEl1" : "secEl2";
  const base = element ? id.replace(/[12](?=[A-Z]|$)/, "{n}") : id;
  const elementLabels: Record<string, string> = {
    "mat{n}Tipo": t.element.materialType,
    "mat{n}Specie": t.element.species,
    "mat{n}Classe": t.element.class,
    "clt{n}Lato": t.clt.lato,
    "clt{n}NumStrati": t.clt.numStrati,
    "clt{n}Disposizione": t.clt.disposizione,
    "clt{n}SpessoriMode": t.clt.spessoriMode,
    "clt{n}Spessore0": t.clt.spessoreSingle,
    "clt{n}Spessore90": t.clt.spessore90,
    "clt{n}Classe": t.clt.classe,
    "clt{n}UserLayers": t.clt.userLayers,
    "fi{n}": stripTags(t.element.fiber(n)),
    "eps{n}": stripTags(t.element.eps(n)),
    "b{n}": `b${n}`,
    "d{n}": `d${n}`,
    "spessore{n}": t.element.thickness(n),
    "rhok{n}": "ρk",
    "rhomean{n}": "ρmean",
  };
  const elementLabel = elementLabels[base];
  if (elementLabel !== undefined) return { label: elementLabel, section: elementSection };

  const s = t.loadsSection;
  const other: Record<string, { label: string; section: InputSectionId }> = {
    thetaNum: { label: t.typeSection.theta, section: "secTipologia" },
    forceFn: { label: s.fn, section: "secCarichi" },
    forceFv: { label: s.fv, section: "secCarichi" },
    forceFa: { label: s.fa, section: "secCarichi" },
    classeServizio: { label: s.serviceClass, section: "secCarichi" },
    durataCarico: { label: s.loadDuration, section: "secCarichi" },
    normativaGammaM: { label: stripTags(s.gammaMRef), section: "secCarichi" },
    gammaMColonna: { label: s.column, section: "secCarichi" },
    gammaM: { label: "γM", section: "secCarichi" },
    etaForo: { label: t.connectionSection.foro, section: "secConnessione" },
  };
  return other[id] ?? { label: id, section: "secTipologia" };
}
